// App-wide context: the public config and the current session user, loaded once
// and refreshable after login/logout.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, type AppConfig, type SessionUser } from '../api';

interface AppContextValue {
  config: AppConfig | null;
  user: SessionUser | null;
  loading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
  setHardware: (label: string | null) => void;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshUser = useCallback(async () => {
    try {
      const { user: u } = await api.me();
      setUser(u);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cfg, me] = await Promise.all([api.config(), api.me()]);
        if (cancelled) return;
        setConfig(cfg);
        setUser(me.user);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setHardware = useCallback((label: string | null) => {
    setConfig((c) => (c ? { ...c, hardware: label || null } : c));
  }, []);

  const value = useMemo(
    () => ({ config, user, loading, error, refreshUser, setHardware }),
    [config, user, loading, error, refreshUser, setHardware],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
